import React, { useState, useEffect, useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  CircularProgress,
  Alert,
  Divider,
  Menu,
  ListItemIcon,
  ListItemText,
  InputAdornment,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import VideoLibraryIcon from "@mui/icons-material/VideoLibrary";
import AddIcon from "@mui/icons-material/Add";
import PersonIcon from "@mui/icons-material/Person";
import EmailIcon from "@mui/icons-material/Email";
import PhoneIcon from "@mui/icons-material/Phone";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import DataObjectIcon from "@mui/icons-material/DataObject";
import axios from "axios";
import { hostname } from "../utils";
import LoadingButton from "./LoadingButton";

interface HeyGenTemplate {
  template_id: string;
  name?: string;
  thumbnail_url?: string;
  variables?: Array<{
    name: string;
    type: string;
    properties?: {
      content?: string;
    };
  }>;
}

interface HeyGenIntegration {
  id: string;
  instanceName?: string;
  type: string;
}

export interface HeyGenVideoConfig {
  integrationId: string;
  templateId: string;
  templateName: string;
  variables: Record<string, string>; // HeyGen variable name -> value (can include {{placeholders}})
}

interface VariableField {
  key: string;
  label: string;
}

interface HeyGenVideoDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (config: HeyGenVideoConfig, placeholder: string) => void;
  getToken: () => Promise<string>;
  companyId: string;
  heygenIntegrations: HeyGenIntegration[];
  existingConfig?: HeyGenVideoConfig | null;
  customVariableFields?: VariableField[];
}

// Standard member fields - same as EmailTemplateEditor
const STANDARD_MEMBER_FIELDS = [
  { key: "firstName", label: "First Name", icon: <PersonIcon fontSize="small" /> },
  { key: "lastName", label: "Last Name", icon: <PersonIcon fontSize="small" /> },
  { key: "email", label: "Email", icon: <EmailIcon fontSize="small" /> },
  { key: "phone", label: "Phone", icon: <PhoneIcon fontSize="small" /> },
  { key: "linkedinUrl", label: "LinkedIn URL", icon: <LinkedInIcon fontSize="small" /> },
];

const HeyGenVideoDialog: React.FC<HeyGenVideoDialogProps> = ({
  open,
  onClose,
  onSave,
  getToken,
  companyId,
  heygenIntegrations,
  existingConfig,
  customVariableFields = [],
}) => {
  const [selectedIntegrationId, setSelectedIntegrationId] = useState("");
  const [templates, setTemplates] = useState<HeyGenTemplate[]>([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState("");
  const [variables, setVariables] = useState<Record<string, string>>({});
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  // For variable insertion menu
  const [variableMenuAnchorEl, setVariableMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [activeVariableField, setActiveVariableField] = useState<string | null>(null);
  const inputRefs = useRef<Record<string, HTMLInputElement | null>>({});

  // Filter custom fields to exclude standard ones
  const uniqueCustomFields = customVariableFields.filter(
    (field) => !STANDARD_MEMBER_FIELDS.map((f) => f.key).includes(field.key)
  );

  // Load existing config when dialog opens
  useEffect(() => {
    if (open && existingConfig) {
      setSelectedIntegrationId(existingConfig.integrationId);
      setSelectedTemplateId(existingConfig.templateId);
      setVariables(existingConfig.variables);
    } else if (open && !existingConfig) {
      // Reset state when opening without existing config
      setSelectedIntegrationId("");
      setSelectedTemplateId("");
      setVariables({});
    }
  }, [open, existingConfig]);

  // Auto-select if only one integration
  useEffect(() => {
    if (heygenIntegrations.length === 1 && !selectedIntegrationId) {
      setSelectedIntegrationId(heygenIntegrations[0].id);
    }
  }, [heygenIntegrations, selectedIntegrationId]);

  // Load templates when integration changes
  useEffect(() => {
    if (!selectedIntegrationId || !open) return;

    const loadTemplates = async () => {
      setLoadingTemplates(true);
      setError("");
      setTemplates([]);

      try {
        const token = await getToken();
        const integration = heygenIntegrations.find(
          (i) => i.id === selectedIntegrationId
        );
        if (!integration) return;

        // Get API key from integration to fetch templates
        const response = await axios.get(
          `${hostname}/integrations/${selectedIntegrationId}`,
          {
            params: { companyId },
            headers: { Authorization: `Bearer ${token}` },
          }
        );

        const apiKey = response.data?.integration?.parameters?.apiKey;
        if (!apiKey) {
          setError("HeyGen API key not configured");
          return;
        }

        // Fetch templates using the API key
        const templatesResponse = await axios.post(
          `${hostname}/integrations/heygen/templates`,
          { apiKey },
          { headers: { Authorization: `Bearer ${token}` } }
        );

        setTemplates(templatesResponse.data.templates || []);
      } catch (err: any) {
        setError(err.response?.data?.error || "Failed to load templates");
      } finally {
        setLoadingTemplates(false);
      }
    };

    loadTemplates();
  }, [selectedIntegrationId, open, getToken, companyId, heygenIntegrations]);

  // Update variables when template changes (but preserve existing values if editing)
  useEffect(() => {
    if (!selectedTemplateId) {
      if (!existingConfig) {
        setVariables({});
      }
      return;
    }

    const template = templates.find((t) => t.template_id === selectedTemplateId);
    if (template?.variables) {
      const initialVars: Record<string, string> = {};
      template.variables.forEach((v) => {
        // Only text variables can have personalized content
        if (v.type === "text") {
          // Use existing value if available, otherwise use default
          initialVars[v.name] = variables[v.name] ?? v.properties?.content ?? "";
        }
      });
      setVariables(initialVars);
    }
  }, [selectedTemplateId, templates]);

  const handleSave = () => {
    if (!selectedIntegrationId || !selectedTemplateId) return;

    setSaving(true);
    const template = templates.find((t) => t.template_id === selectedTemplateId);

    const config: HeyGenVideoConfig = {
      integrationId: selectedIntegrationId,
      templateId: selectedTemplateId,
      templateName: template?.name || "",
      variables,
    };

    // Generate placeholder with embedded variable mappings
    // Format: {{HeyGenVideo: var1="value1", var2="value2"}}
    const variableParts = Object.entries(variables)
      .filter(([_, value]) => value) // Only include non-empty values
      .map(([key, value]) => `${key}="${value}"`)
      .join(", ");

    const placeholder = variableParts
      ? `{{HeyGenVideo: ${variableParts}}}`
      : "{{HeyGenVideo}}";

    onSave(config, placeholder);
    setSaving(false);
    onClose();
  };

  const handleOpenVariableMenu = (event: React.MouseEvent<HTMLElement>, fieldName: string) => {
    setActiveVariableField(fieldName);
    setVariableMenuAnchorEl(event.currentTarget);
  };

  const handleInsertVariable = (variableKey: string) => {
    if (activeVariableField) {
      const placeholder = `{{${variableKey}}}`;
      setVariables((prev) => ({
        ...prev,
        [activeVariableField]: (prev[activeVariableField] || "") + placeholder,
      }));
      // Focus back on the input
      setTimeout(() => {
        inputRefs.current[activeVariableField]?.focus();
      }, 0);
    }
    setVariableMenuAnchorEl(null);
    setActiveVariableField(null);
  };

  const selectedTemplate = templates.find(
    (t) => t.template_id === selectedTemplateId
  );
  // Text variables contain script content that can be personalized
  const textVariables =
    selectedTemplate?.variables?.filter((v) => v.type === "text") || [];
  // Voice variables are for voice selection, not content
  const voiceVariables =
    selectedTemplate?.variables?.filter((v) => v.type === "voice") || [];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <VideoLibraryIcon color="primary" />
            <Typography variant="h6">
              {existingConfig ? "Edit HeyGen Video" : "Add HeyGen Video"}
            </Typography>
          </Box>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Alert severity="info" sx={{ mb: 2 }}>
          Configure a personalized video template. A unique video will be generated
          for each contact when the email is sent, using their personal information.
        </Alert>

        {heygenIntegrations.length === 0 ? (
          <Alert severity="warning">
            No HeyGen integrations found. Please add a HeyGen integration first.
          </Alert>
        ) : (
          <>
            {/* Integration Selection */}
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>HeyGen Account</InputLabel>
              <Select
                value={selectedIntegrationId}
                onChange={(e) => {
                  setSelectedIntegrationId(e.target.value);
                  setSelectedTemplateId("");
                  setVariables({});
                }}
                label="HeyGen Account"
              >
                {heygenIntegrations.map((integration) => (
                  <MenuItem key={integration.id} value={integration.id}>
                    {integration.instanceName || "HeyGen"}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Template Selection */}
            {selectedIntegrationId && (
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Video Template</InputLabel>
                <Select
                  value={selectedTemplateId}
                  onChange={(e) => setSelectedTemplateId(e.target.value)}
                  label="Video Template"
                  disabled={loadingTemplates}
                >
                  {loadingTemplates ? (
                    <MenuItem disabled>
                      <CircularProgress size={16} sx={{ mr: 1 }} />
                      Loading templates...
                    </MenuItem>
                  ) : templates.length === 0 ? (
                    <MenuItem disabled>No templates available</MenuItem>
                  ) : (
                    templates.map((template) => (
                      <MenuItem key={template.template_id} value={template.template_id}>
                        {template.name || template.template_id}
                      </MenuItem>
                    ))
                  )}
                </Select>
              </FormControl>
            )}

            {/* Template Variables */}
            {selectedTemplateId && (
              <>
                <Divider sx={{ my: 2 }} />

                {textVariables.length > 0 ? (
                  <>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                      Template Variables
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      Map each video variable to contact data. Click the + button to insert
                      personalization variables.
                    </Typography>

                    {textVariables.map((variable) => (
                      <TextField
                        key={variable.name}
                        fullWidth
                        label={variable.name}
                        value={variables[variable.name] || ""}
                        onChange={(e) =>
                          setVariables((prev) => ({
                            ...prev,
                            [variable.name]: e.target.value,
                          }))
                        }
                        inputRef={(el) => { inputRefs.current[variable.name] = el; }}
                        multiline={variable.name.toLowerCase().includes("script")}
                        rows={variable.name.toLowerCase().includes("script") ? 3 : 1}
                        sx={{ mb: 2 }}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                size="small"
                                onClick={(e) => handleOpenVariableMenu(e, variable.name)}
                                title="Insert Variable"
                              >
                                <AddIcon fontSize="small" />
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                      />
                    ))}

                    {/* Variable insertion menu */}
                    <Menu
                      anchorEl={variableMenuAnchorEl}
                      open={Boolean(variableMenuAnchorEl)}
                      onClose={() => { setVariableMenuAnchorEl(null); setActiveVariableField(null); }}
                      PaperProps={{ sx: { minWidth: 200, maxHeight: 300 } }}
                    >
                      <Typography variant="caption" sx={{ px: 2, py: 0.5, display: "block", color: "text.secondary" }}>
                        Member Fields
                      </Typography>
                      {STANDARD_MEMBER_FIELDS.map((field) => (
                        <MenuItem key={field.key} onClick={() => handleInsertVariable(field.key)} dense>
                          <ListItemIcon>{field.icon}</ListItemIcon>
                          <ListItemText
                            primary={field.label}
                            secondary={`{{${field.key}}}`}
                            secondaryTypographyProps={{ variant: "caption", fontFamily: "monospace" }}
                          />
                        </MenuItem>
                      ))}
                      {uniqueCustomFields.length > 0 && (
                        <>
                          <Divider sx={{ my: 1 }} />
                          <Typography variant="caption" sx={{ px: 2, py: 0.5, display: "block", color: "text.secondary" }}>
                            Custom Fields
                          </Typography>
                          {uniqueCustomFields.map((field) => (
                            <MenuItem key={field.key} onClick={() => handleInsertVariable(field.key)} dense>
                              <ListItemIcon><DataObjectIcon fontSize="small" /></ListItemIcon>
                              <ListItemText
                                primary={field.label}
                                secondary={`{{${field.key}}}`}
                                secondaryTypographyProps={{ variant: "caption", fontFamily: "monospace" }}
                              />
                            </MenuItem>
                          ))}
                        </>
                      )}
                    </Menu>
                  </>
                ) : (
                  <Alert severity="warning" sx={{ mt: 1 }}>
                    This template has no text variables for personalization.
                    {voiceVariables.length > 0 && (
                      <Typography variant="body2" sx={{ mt: 1 }}>
                        Found {voiceVariables.length} voice variable(s): {voiceVariables.map(v => v.name).join(", ")}.
                        Voice variables control which voice is used, not the script content.
                      </Typography>
                    )}
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      To personalize video content, create text variables in your HeyGen template
                      (e.g., for script, names, or other dynamic content).
                    </Typography>
                  </Alert>
                )}
              </>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="inherit">
          Cancel
        </Button>
        <LoadingButton
          variant="contained"
          onClick={handleSave}
          loading={saving}
          disabled={
            !selectedIntegrationId ||
            !selectedTemplateId ||
            loadingTemplates ||
            heygenIntegrations.length === 0
          }
        >
          {existingConfig ? "Update Video Config" : "Save Video Config"}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

export default HeyGenVideoDialog;
